import { displayText } from '../../terminology/store';
import { useTerminology } from '../../terminology/TerminologyContext';
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeContext from '../ThemeContext';
import Layout from '../Layout';
import { toast } from 'react-toastify';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import ClientConfig from '../../config/Clientconfig';
import { FULL_ROUTES, USER_ROLES } from '../../config/Routesconfig';

import {
  getCsrfToken,
  getCases,
  getDepositionsByCase,
  getArbitrationsByCase,
  getTrialsByCase,
  getMediationsByCase,
  getWitnessPrepsByCase,
  getCustomSessionsByCase,
} from '../services/api';

const CASE_FIELDS      = ClientConfig.cases.fields;           // id, name, number
const SESSION_FIELDS   = ClientConfig.sessions.fields;        // id, displayName, sessionType …
const CASE_HEADER_OPTS = ClientConfig.cases.headerOptions;    // [{ label, sortField }]
const SESS_HEADER_OPTS = ClientConfig.sessions.headerOptions; // [{ label, sortField }]
const FILTER_OPTS      = ClientConfig.sessions.filterOptions; // [{ label, match }]
const STATUS_DISPLAY   = ClientConfig.sessions.statusDisplay; // { pending, started, finished }
const SESSION_TYPES    = ClientConfig.sessions.category.options; // [{ value, normalizedType, label }]
const SESSION_TYPE_MAP = ClientConfig.sessions.types;         // { deposition: { label }, … }


const SESSION_FETCHERS = {
  deposition:   getDepositionsByCase,
  arbitration:  getArbitrationsByCase,
  trial:        getTrialsByCase,
  mediation:    getMediationsByCase,
  witness_prep: getWitnessPrepsByCase,
  custom:       getCustomSessionsByCase,
};


const getDisplayLabel = (typeKey, session) => {
  if (typeKey === 'custom') {
    return session?.custom_type?.trim() || SESSION_TYPE_MAP.custom.label;
  }
  return SESSION_TYPE_MAP[typeKey]?.label || typeKey;
};


const mapSession = (rawArr, typeCfg) => {
  const arr = Array.isArray(rawArr) ? rawArr : rawArr?.data || [];

  return arr
    .filter((s) =>
      (s[SESSION_FIELDS.sessionType] || '').toLowerCase().trim() === typeCfg.value
    )
    .map((s) => {
      const displayType = getDisplayLabel(typeCfg.value, s);
      return {
        ...s,
     
        [SESSION_FIELDS.sessionType]: displayType,
        displayType,
        normalizedType: typeCfg.normalizedType,
        
        [SESSION_FIELDS.displayName]:
          s.witness || s.name || s.custom_type ||
          `Unnamed ${SESSION_TYPE_MAP[typeCfg.value]?.label || typeCfg.value}`,
        [SESSION_FIELDS.createdAt]:
          s[SESSION_FIELDS.createdAt] || new Date().toISOString(),
        [SESSION_FIELDS.status]:
          s[SESSION_FIELDS.status] || 'None',
      };
    });
};


const fetchAllSessionsForCase = async (caseId) => {
  const results = await Promise.all(
    SESSION_TYPES.map((typeCfg) =>
      (SESSION_FETCHERS[typeCfg.value]?.(caseId) ?? Promise.resolve({ data: [] }))
        .catch(() => ({ data: [] }))
    )
  );

  const all = results.flatMap((res, i) => mapSession(res.data, SESSION_TYPES[i]));


  return Array.from(
    new Map(
      all.map((s) => [`${s[SESSION_FIELDS.id]}-${s.normalizedType}`, s])
    ).values()
  );
};

const applyFilterAndSort = (list, filterLabel, sortField, sortOrder) => {
  const filterCfg = FILTER_OPTS.find((o) => o.label === filterLabel);
  let filtered = [...list];

  if (filterCfg?.match) {
    filtered = filtered.filter(
      (s) => (s[SESSION_FIELDS.status] || '').toLowerCase() === filterCfg.match
    );
  }

  return filtered.sort((a, b) => {
    const valA = (a[sortField] || '').toString().toLowerCase();
    const valB = (b[sortField] || '').toString().toLowerCase();
    const cmp  = valA < valB ? -1 : valA > valB ? 1 : 0;
    return sortOrder === 'asc' ? cmp : -cmp;
  });
};


const mapStatusDisplay = (status) => {
  const key = (status || '').toLowerCase();
  return STATUS_DISPLAY[key] ?? (status === 'None' ? 'N/A' : status);
};

const defaultTheme = {
  name: 'default',
  colors: {
    primary:     '#f97316',
    secondary:   '#6c757d',
    bgPrimary:   '#ffffff',
    textWhite:   '#ffffff',
    textPrimary: '#000000',
    borderColor: '#dee2e6',
  },
  gradients: {
    primary:   '#0d6efd',
    secondary: '#6c757d',
  },
  typography: {
    fontFamily:    '"Roboto", "Helvetica Neue", Arial, sans-serif',
    titleFontSize: 24,
    bodyFontSize:  14,
  },
};

const Session = () => {
  useTerminology();
  const { theme = defaultTheme } = useContext(ThemeContext);

  const fontFamily    = theme.typography?.fontFamily    || defaultTheme.typography.fontFamily;
  const bodyFontSize  = theme.typography?.bodyFontSize  || defaultTheme.typography.bodyFontSize;
  const titleFontSize = theme.typography?.titleFontSize || defaultTheme.typography.titleFontSize;

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [cases,       setCases]       = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [sessions,    setSessions]    = useState([]);


  const [caseSortField,    setCaseSortField]    = useState(CASE_HEADER_OPTS[0].sortField);
  const [caseSortOrder,    setCaseSortOrder]    = useState('asc');
  const [sessionSortField, setSessionSortField] = useState(SESS_HEADER_OPTS[0].sortField);
  const [sessionSortOrder, setSessionSortOrder] = useState('asc');


  const [caseHeaderOption,    setCaseHeaderOption]    = useState(CASE_HEADER_OPTS[0].label);
  const [sessionHeaderOption, setSessionHeaderOption] = useState(SESS_HEADER_OPTS[0].label);

  const [selectedCaseId, setSelectedCaseId] = useState(null);


  const [filter, setFilter] = useState(FILTER_OPTS[0].label);

 
  const [showFilterDropdown,    setShowFilterDropdown]    = useState(false);
  const [showCaseHeaderMenu,    setShowCaseHeaderMenu]    = useState(false);
  const [showSessionHeaderMenu, setShowSessionHeaderMenu] = useState(false);
  const filterDropdownRef  = useRef(null);
  const caseHeaderRef      = useRef(null);
  const sessionHeaderRef   = useRef(null);


  useEffect(() => {
    if (!user?.username) {
      toast.error('Please log in to continue.', { autoClose: 3000 });
      navigate(FULL_ROUTES.login);
    }
  }, [user, navigate]);


  useEffect(() => {
    const fetchCases = async () => {
      try {
        await getCsrfToken();
        const res = await getCases();
        setCases(res.data.data || res.data || []);
      } catch (error) {
        toast.error(
          displayText('Failed to fetch cases: ') + (error.response?.data?.message || error.message),
          { autoClose: 3000 }
        );
      }
    };
    fetchCases();
  }, []);

 
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterDropdownRef.current  && !filterDropdownRef.current.contains(e.target))  setShowFilterDropdown(false);
      if (caseHeaderRef.current      && !caseHeaderRef.current.contains(e.target))      setShowCaseHeaderMenu(false);
      if (sessionHeaderRef.current   && !sessionHeaderRef.current.contains(e.target))   setShowSessionHeaderMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (allSessions.length > 0) {
      setSessions(applyFilterAndSort(allSessions, filter, sessionSortField, sessionSortOrder));
    }
  }, [filter, sessionSortField, sessionSortOrder, allSessions]);

  const fetchSessionsForCase = async (caseId) => {
    try {
      const uniq = await fetchAllSessionsForCase(caseId);
      setAllSessions(uniq);
      setSessions(applyFilterAndSort(uniq, filter, sessionSortField, sessionSortOrder));
      setSelectedCaseId(caseId);
    } catch (error) {
      toast.error(
        displayText('Failed to fetch sessions: ') + (error.response?.data?.message || error.message),
        { autoClose: 3000 }
      );
      setAllSessions([]);
      setSessions([]);
    }
  };

  const fetchAllSessions = async () => {
    try {
      const perCase = await Promise.all(
        cases.map((c) =>
          fetchAllSessionsForCase(c[CASE_FIELDS.id]).catch(() => [])
        )
      );
      const flat = perCase.flat();
      const sessionMap = new Map();
      flat.forEach((s) => {
        const key = `${s[SESSION_FIELDS.id]}-${s.normalizedType}`;
        if (!sessionMap.has(key)) sessionMap.set(key, s);
      });
      const uniq = Array.from(sessionMap.values());
      setAllSessions(uniq);
      setSessions(applyFilterAndSort(uniq, filter, sessionSortField, sessionSortOrder));
      setSelectedCaseId(null);
    } catch (error) {
      toast.error(
        displayText('Failed to fetch sessions: ') + (error.response?.data?.message || error.message),
        { autoClose: 3000 }
      );
      setAllSessions([]);
      setSessions([]);
    }
  };

  const handleSort = (field, type) => {
    if (type === 'case') {
      setCaseSortField(field);
      setCaseSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSessionSortField(field);
      setSessionSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    }
  };


  const handleCaseHeaderOptionChange = (label) => {
    const cfg = CASE_HEADER_OPTS.find((o) => o.label === label);
    if (!cfg) return;
    setCaseHeaderOption(cfg.label);
    setCaseSortField(cfg.sortField);
    setShowCaseHeaderMenu(false);
  };

  
  const handleSessionHeaderOptionChange = (label) => {
    const cfg = SESS_HEADER_OPTS.find((o) => o.label === label);
    if (!cfg) return;
    setSessionHeaderOption(cfg.label);
    setSessionSortField(cfg.sortField);
    setShowSessionHeaderMenu(false);
  };

  const handleFilterChange = (label) => {
    setFilter(label);
    setShowFilterDropdown(false);
  };


  const sortedCases = [...cases].sort((a, b) => {
    const va = (a[caseSortField] || '').toString().toLowerCase();
    const vb = (b[caseSortField] || '').toString().toLowerCase();
    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
    return caseSortOrder === 'asc' ? cmp : -cmp;
  });

  const handleJoinClick = (sessionId, sessionType) => {
    localStorage.setItem(
      'currentSession',
      JSON.stringify({
        sessionId: String(sessionId),
        type:      sessionType,
   
        userRole:  user.role === USER_ROLES.clientAdmin ? USER_ROLES.clientAdmin : 'member',
      })
    );
    navigate(FULL_ROUTES.joinSession);
  };

  
  return (
    <Layout>
      <div
        style={{
          fontFamily,
          fontSize:        `${bodyFontSize}px`,
          width:           '100%',
          boxSizing:       'border-box',
          backgroundColor: theme.colors.bgPrimary,
          color:           theme.colors.textPrimary,
          maxWidth:        '100%',
          margin:          '0 auto',
        }}
      >
        <div
          style={{
            marginTop:       'clamp(0.5rem, 2vw, 1rem)',
            marginBottom:    'clamp(0.5rem, 2vw, 1rem)',
            display:         'flex',
            flexDirection:   window.innerWidth < 600 ? 'column' : 'row',
            width:           '100%',
            height:          '500px',
            overflowY:       'auto',
            borderRadius:    '10px',
            backgroundColor: '#ffffff',
            border:          '1px solid #dee2e6',
          }}
        >

          <div
            style={{
              width:        window.innerWidth < 600 ? '100%' : '15%',
              borderRight:  window.innerWidth < 600 ? 'none' : '1px solid #dee2e6',
              borderBottom: window.innerWidth < 600 ? '1px solid #dee2e6' : 'none',
            }}
          >
            <h4
              style={{
                fontSize:   `${titleFontSize}px`,
                fontWeight: 'bold',
                color:      theme.colors.textPrimary,
                margin:     '0.5rem 1rem',
                display:    'flex',
                alignItems: 'center',
                gap:        '0.5rem',
              }}
            >
              <i className="bi bi-folder" style={{ color: theme.colors.primary, fontSize: '1.2em' }} />
              {ClientConfig.cases.pluralLabel}
            </h4>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                  <th
                    ref={caseHeaderRef}
                    style={{ padding: '0.5rem 1rem', textAlign: 'left', fontSize: `${bodyFontSize}px`, position: 'relative' }}
                  >
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: theme.colors.textWhite }}
                        onClick={(e) => { e.stopPropagation(); setShowCaseHeaderMenu((p) => !p); }}
                      >
                        {caseHeaderOption}
                      </span>
                      <span
                        style={{ cursor: 'pointer', marginLeft: '4px' }}
                        onClick={(e) => { e.stopPropagation(); handleSort(caseSortField, 'case'); }}
                      >
                        <i className={`bi ${caseSortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'}`} />
                      </span>
                    </div>

                
                    {showCaseHeaderMenu && (
                      <div style={dropdownStyle(theme)}>
                        {CASE_HEADER_OPTS.map((opt) => (
                          <div
                            key={opt.label}
                            style={dropdownItemStyle(caseHeaderOption === opt.label)}
                            onClick={() => handleCaseHeaderOptionChange(opt.label)}
                          >
                            {opt.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortedCases.length > 0 ? (
                  sortedCases.map((caseItem) => (
                    <React.Fragment key={caseItem[CASE_FIELDS.id]}>
                      <tr
                        style={{
                          backgroundColor: selectedCaseId === caseItem[CASE_FIELDS.id] ? '#f0f0f0' : '#ffffff',
                          cursor: 'pointer',
                        }}
                        onClick={() => fetchSessionsForCase(caseItem[CASE_FIELDS.id])}
                      >
                        <td style={{ padding: '0.5rem 1rem', fontSize: `${bodyFontSize}px` }}>
                        
                          <span style={{ color: theme.colors.primary, fontWeight: 'bold' }}>
                            {caseItem[CASE_FIELDS.name]}
                          </span>
                          <br />
                       
                          <span style={{ color: theme.colors.textPrimary }}>{displayText(" Case #: ")}{caseItem[CASE_FIELDS.number] || 'N/A'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: 0 }}>
                          <hr style={{ border: '1px solid #dee2e6', margin: 0 }} />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td style={{ padding: '0.5rem 1rem', fontSize: `${bodyFontSize}px` }}>
                  
                      {ClientConfig.cases.emptyMessage}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>


          <div style={{ width: window.innerWidth < 600 ? '100%' : '85%' }}>
            <h4
              style={{
                fontSize:   `${titleFontSize}px`,
                fontWeight: 'bold',
                color:      theme.colors.textPrimary,
                margin:     '0.5rem 1rem',
                display:    'flex',
                alignItems: 'center',
                gap:        '0.5rem',
              }}
            >
              <i className="bi bi-file-earmark" style={{ color: theme.colors.primary, fontSize: '1.2em' }} />
          
              {ClientConfig.sessions.pluralLabel}
            </h4>

            <div style={{ position: 'relative' }} ref={filterDropdownRef}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: theme.gradients.primary, color: theme.colors.textWhite }}>
                    <th
                      ref={sessionHeaderRef}
                      style={{ padding: '0.5rem 1rem', textAlign: 'left', fontSize: `${bodyFontSize}px`, position: 'relative' }}
                    >
                      {/* Sort-field label */}
                      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: theme.colors.textWhite }}
                          onClick={(e) => { e.stopPropagation(); setShowSessionHeaderMenu((p) => !p); }}
                        >
                          {sessionHeaderOption}
                        </span>
                        <span
                          style={{ cursor: 'pointer', marginLeft: '4px' }}
                          onClick={(e) => { e.stopPropagation(); handleSort(sessionSortField, 'session'); }}
                        >
                          <i className={`bi ${sessionSortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'}`} />
                        </span>
                      </div>

                      {/* Filter label — top-right of header */}
                      <span
                        style={{
                          position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                          cursor: 'pointer', textDecoration: 'underline', color: theme.colors.textWhite,
                        }}
                        onClick={(e) => { e.stopPropagation(); setShowFilterDropdown((p) => !p); }}
                      >
                        {filter}
                      </span>

                      
                      {showSessionHeaderMenu && (
                        <div style={dropdownStyle(theme)}>
                          {SESS_HEADER_OPTS.map((opt) => (
                            <div
                              key={opt.label}
                              style={dropdownItemStyle(sessionHeaderOption === opt.label)}
                              onClick={() => handleSessionHeaderOptionChange(opt.label)}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}

                  
                      {showFilterDropdown && (
                        <div style={{ ...dropdownStyle(theme), right: 0, left: 'auto' }}>
                          {FILTER_OPTS.map((opt) => (
                            <div
                              key={opt.label}
                              style={dropdownItemStyle(filter === opt.label)}
                              onClick={() => handleFilterChange(opt.label)}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sessions.length > 0 ? (
                    sessions.map((session) => (
                      <React.Fragment key={`${session[SESSION_FIELDS.id]}-${session.normalizedType}`}>
                        <tr style={{ backgroundColor: '#ffffff' }}>
                          <td
                            style={{
                              padding:        '0.5rem 1rem',
                              fontSize:       `${bodyFontSize}px`,
                              verticalAlign:  'top',
                              display:        'flex',
                              justifyContent: 'space-between',
                              alignItems:     'center',
                            }}
                          >
                          
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div>
                              
                                <span style={{ color: theme.colors.primary, fontWeight: 'bold' }}>
                                  {session[SESSION_FIELDS.displayName]}
                                </span>
                                <br />
                             
                                <span style={{ color: theme.colors.textPrimary }}>
                                  {session[SESSION_FIELDS.sessionType]}
                                </span>
                              </div>
                              {(session[SESSION_FIELDS.status] || '').toLowerCase() === 'started' && (
                                <i
                                  className="bi bi-record2"
                                  style={{ color: 'red', fontSize: '1.2em' }}
                                  title={displayText("Session in progress")}
                                />
                              )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                             
                              <span style={{ color: theme.colors.textPrimary }}>
                                {mapStatusDisplay(session[SESSION_FIELDS.status])}
                              </span>

                              <div style={{ textAlign: 'center' }}>
                           
                                <span style={{ color: theme.colors.textPrimary }}>
                                  Volume: {session[SESSION_FIELDS.volume] || 'N/A'}
                                </span>
                                <br />
                               
                                <span style={{ color: theme.colors.textPrimary }}>
                                  {new Date(session[SESSION_FIELDS.createdAt]).toLocaleDateString()}
                                </span>
                              </div>

                        
                              <div
                                style={{
                                  cursor:   'pointer',
                                  color:    theme.colors.primary,
                                  fontSize: 'clamp(16px, 2.5vw, 18px)',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleJoinClick(session[SESSION_FIELDS.id], session.normalizedType);
                                }}
                              >
                                <i className="bi bi-chevron-right" />
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: 0 }}>
                            <hr style={{ border: '1px solid #dee2e6', margin: 0 }} />
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td style={{ padding: '0.5rem 1rem', fontSize: `${bodyFontSize}px` }}>
                    
                        {selectedCaseId
                          ? ClientConfig.sessions.emptyMessage
                          : ClientConfig.sessions.selectCasePrompt}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

const dropdownStyle = (theme) => ({
  position:        'absolute',
  top:             '100%',
  left:            '1rem',
  backgroundColor: '#ffffff',
  border:          `1px solid ${theme.colors.borderColor}`,
  borderRadius:    '4px',
  minWidth:        '140px',
  zIndex:          10,
  marginTop:       4,
  boxShadow:       '0 4px 8px rgba(0,0,0,0.1)',
});

const dropdownItemStyle = (isActive) => ({
  padding:         '0.5rem 0.75rem',
  cursor:          'pointer',
  backgroundColor: isActive ? '#ababab' : '#ffffff',
  color:           isActive ? '#ffffff' : '#000000',
});

export default Session;